import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import {
  ArrowClockwise, CheckCircle, XCircle, Warning,
  Lightning, Database, EnvelopeSimple, Timer,
  Funnel, Users, Queue, Robot,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// ── helpers ────────────────────────────────────────────────────────────────

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
}

function num(v, decimals = 0) {
  if (v == null) return '—';
  return typeof v === 'number' ? v.toFixed(decimals) : v;
}

// ── Feature flag badge ──────────────────────────────────────────────────────

function FlagBadge({ label, enabled }) {
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${enabled ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-zinc-700 bg-white/[0.02]'}`}>
      <span className="text-xs text-zinc-400">{label}</span>
      {enabled
        ? <CheckCircle size={14} className="text-emerald-400" weight="fill" />
        : <XCircle size={14} className="text-zinc-600" weight="fill" />}
    </div>
  );
}

// ── Connection pill ─────────────────────────────────────────────────────────

function ConnPill({ label, connected }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${connected ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-red-500/30 text-red-400 bg-red-500/5'}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-red-400'} animate-pulse`} />
      {label}
    </div>
  );
}

// ── Circuit breaker badge ───────────────────────────────────────────────────

const CB_STYLE = {
  closed:    'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  open:      'bg-red-500/10 text-red-400 border-red-500/20',
  half_open: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  unknown:   'bg-zinc-700/50 text-zinc-500 border-zinc-700',
};

function CBBadge({ state }) {
  const s = (state || 'unknown').toLowerCase().replace('-', '_');
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${CB_STYLE[s] || CB_STYLE.unknown}`}>
      {s.replace('_', '-')}
    </span>
  );
}

// ── Sparkline (inline SVG) ──────────────────────────────────────────────────

function Sparkline({ data }) {
  if (!data || data.length === 0) return <div className="text-xs text-zinc-600 italic">No data</div>;

  const W = 260, H = 36, PAD = 2;
  const max = Math.max(...data.map(d => d.emails_sent), 1);
  const barW = (W - PAD * (data.length - 1)) / data.length;

  return (
    <svg width={W} height={H} className="overflow-visible">
      {data.map((d, i) => {
        const h = Math.max(2, (d.emails_sent / max) * (H - 4));
        const x = i * (barW + PAD);
        const y = H - h;
        return (
          <g key={d.date}>
            <rect x={x} y={y} width={barW} height={h}
              fill={d.emails_sent > 0 ? '#F49F0A' : '#27272a'}
              rx={1} opacity={0.85} />
            <title>{d.date}: {d.emails_sent} emails</title>
          </g>
        );
      })}
    </svg>
  );
}

// ── Date range pill selector ────────────────────────────────────────────────

const RANGES = ['today', 'yesterday', '7d', '30d'];

function RangePills({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {RANGES.map(r => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider border transition-colors ${
            value === r
              ? 'bg-amber-500/20 border-amber-500/40 text-amber-400'
              : 'border-zinc-700 text-zinc-500 hover:text-zinc-300 hover:border-zinc-500'
          }`}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────

export default function AdminTelemetryTab() {
  const {
    fetchAdminTelemetry, fetchResendUsage, overrideCircuitBreaker,
    fetchScraperStats, fetchUserActivity, fetchQueueStats,
  } = useApp();

  const [health, setHealth] = useState(null);
  const [llm, setLLM] = useState(null);
  const [resend, setResend] = useState(null);
  const [slowQ, setSlowQ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cbBusy, setCbBusy] = useState('');

  // Scraper / user-activity state
  const [scraperRange, setScraperRange] = useState('7d');
  const [activityRange, setActivityRange] = useState('7d');
  const [scraperStats, setScraperStats] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [scraperLoading, setScraperLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const queueTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, u, r, q] = await Promise.all([
        fetchAdminTelemetry('health'),
        fetchAdminTelemetry('usage'),
        fetchResendUsage(),
        fetchAdminTelemetry('queries'),
      ]);
      setHealth(h);
      setLLM(u);
      setResend(r);
      setSlowQ(q);
    } catch {
      toast.error('Could not load telemetry');
    } finally {
      setLoading(false);
    }
  }, [fetchAdminTelemetry, fetchResendUsage]);

  const loadScraperStats = useCallback(async (range) => {
    setScraperLoading(true);
    const data = await fetchScraperStats(range);
    setScraperStats(data);
    setScraperLoading(false);
  }, [fetchScraperStats]);

  const loadUserActivity = useCallback(async (range) => {
    setActivityLoading(true);
    const data = await fetchUserActivity(range);
    setUserActivity(data);
    setActivityLoading(false);
  }, [fetchUserActivity]);

  const loadQueueStats = useCallback(async () => {
    const data = await fetchQueueStats();
    setQueueStats(data);
  }, [fetchQueueStats]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadScraperStats(scraperRange); }, [scraperRange, loadScraperStats]);
  useEffect(() => { loadUserActivity(activityRange); }, [activityRange, loadUserActivity]);
  useEffect(() => {
    loadQueueStats();
    queueTimerRef.current = setInterval(loadQueueStats, 10000);
    return () => clearInterval(queueTimerRef.current);
  }, [loadQueueStats]);

  const handleCBOverride = async (provider, action) => {
    setCbBusy(`${provider}:${action}`);
    try {
      await overrideCircuitBreaker(provider, action);
      toast.success(`${provider} circuit breaker ${action}d`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Override failed');
    } finally {
      setCbBusy('');
    }
  };

  const flags = health?.v72_features || {};
  const cbStates = health?.circuit_breakers || [];
  const providers = llm?.providers_rich || [];
  const slowQueries = slowQ?.slow_queries || [];

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-white">System Telemetry</h2>
        <button
          onClick={load}
          className={`p-2 hover:bg-white/5 rounded-full transition-colors ${loading ? 'animate-spin' : ''}`}
        >
          <ArrowClockwise size={16} />
        </button>
      </div>

      {/* Row 1: Feature flags + connectivity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Feature flags */}
        <div className="jp-card p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
            <Lightning size={12} /> v7.2 Feature Flags
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <FlagBadge label="System Logging" enabled={flags.system_logging} />
            <FlagBadge label="Circuit Breaker" enabled={flags.circuit_breaker} />
            <FlagBadge label="Cache Service" enabled={flags.cache_service} />
            <FlagBadge label="Query Profiler" enabled={flags.query_profiler} />
          </div>
        </div>

        {/* Connectivity */}
        <div className="jp-card p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
            <Database size={12} /> Service Connectivity
          </h3>
          <div className="flex flex-wrap gap-2">
            <ConnPill label="Postgres" connected={health?.db === 'connected'} />
            <ConnPill label="Redis" connected={health?.redis?.connected ?? false} />
            <ConnPill label="pgvector" connected={health?.db === 'connected'} />
            <ConnPill label={`Cache (${health?.cache_backend || 'off'})`} connected={health?.cache_enabled ?? false} />
          </div>
          {health?.disk && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
                <span>Disk Usage</span>
                <span>{health.disk.used_gb} GB / {health.disk.total_gb} GB</span>
              </div>
              <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${health.disk.used_percent > 85 ? 'bg-red-500' : health.disk.used_percent > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${health.disk.used_percent}%` }}
                />
              </div>
              <div className="text-[10px] text-zinc-600 mt-0.5">{health.disk.used_percent}% used</div>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: LLM Usage + Circuit Breakers */}
      <div className="jp-card overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
            <Lightning size={12} /> LLM Usage & Circuit Breakers
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="bg-white/[0.02] text-zinc-500">
                <th className="px-4 py-2 font-semibold">Provider / Model</th>
                <th className="px-4 py-2 font-semibold text-right">Today</th>
                <th className="px-4 py-2 font-semibold text-right">7d</th>
                <th className="px-4 py-2 font-semibold text-right">30d calls</th>
                <th className="px-4 py-2 font-semibold text-right">30d tokens</th>
                <th className="px-4 py-2 font-semibold text-right">30d cost</th>
                <th className="px-4 py-2 font-semibold">Circuit Breaker</th>
                <th className="px-4 py-2 font-semibold">Override</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {providers.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-zinc-600 italic">
                    {loading ? 'Loading…' : 'No LLM usage recorded yet'}
                  </td>
                </tr>
              )}
              {providers.map(p => {
                const cb = p.circuit_breaker || {};
                const busy = cbBusy === `${p.provider}:open` || cbBusy === `${p.provider}:close`;
                return (
                  <tr key={`${p.provider}::${p.model}`} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium uppercase">{p.provider}</div>
                      <div className="text-zinc-500 font-mono">{p.model}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-300">{num(p.today?.calls)}</td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-300">{num(p.week?.calls)}</td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-300">{num(p.month?.calls)}</td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-400">
                      {p.month?.tokens ? (p.month.tokens / 1000).toFixed(1) + 'k' : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-amber-400">
                      {p.month?.cost_usd != null ? `$${p.month.cost_usd.toFixed(4)}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <CBBadge state={cb.state} />
                        {cb.failure_count > 0 && (
                          <div className="text-[10px] text-zinc-500">{cb.failure_count} failures</div>
                        )}
                        {cb.last_failure_at && (
                          <div className="text-[10px] text-zinc-600">{fmt(cb.last_failure_at)}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleCBOverride(p.provider, 'close')}
                          disabled={busy || cb.state === 'closed'}
                          className="px-2 py-1 text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/20 disabled:opacity-30 transition-colors"
                        >
                          Force Open
                        </button>
                        <button
                          onClick={() => handleCBOverride(p.provider, 'open')}
                          disabled={busy || cb.state === 'open'}
                          className="px-2 py-1 text-[10px] font-bold bg-red-500/10 text-red-400 border border-red-500/20 rounded hover:bg-red-500/20 disabled:opacity-30 transition-colors"
                        >
                          Force Close
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Row 2b: Scraper Performance */}
      <div className="jp-card overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
            <Funnel size={12} /> Scraper Performance
          </h3>
          <RangePills value={scraperRange} onChange={setScraperRange} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="bg-white/[0.02] text-zinc-500">
                <th className="px-4 py-2 font-semibold">Source</th>
                <th className="px-4 py-2 font-semibold text-right">Found</th>
                <th className="px-4 py-2 font-semibold text-right">Indexed</th>
                <th className="px-4 py-2 font-semibold text-right">Accuracy</th>
                <th className="px-4 py-2 font-semibold text-right">Runs</th>
                <th className="px-4 py-2 font-semibold text-right">Skipped</th>
                <th className="px-4 py-2 font-semibold text-right">Errors</th>
                <th className="px-4 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {scraperLoading && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-zinc-600 italic">Loading…</td></tr>
              )}
              {!scraperLoading && (!scraperStats?.sources?.length) && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-zinc-600 italic">No scraper runs recorded for this period</td></tr>
              )}
              {!scraperLoading && scraperStats?.sources?.map(s => (
                <tr key={s.source} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-2.5 font-mono text-zinc-300">{s.source}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{s.jobs_found}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-white">{s.jobs_indexed}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-amber-400">
                    {s.jobs_found > 0 ? `${s.accuracy_rate}%` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{s.run_count}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-500">{s.skip_count}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-400">{s.error_count || '—'}</td>
                  <td className="px-4 py-2.5">
                    {s.enabled
                      ? <span className="px-2 py-0.5 rounded text-[10px] font-bold border border-emerald-500/20 text-emerald-400 bg-emerald-500/5">ON</span>
                      : <span className="px-2 py-0.5 rounded text-[10px] font-bold border border-zinc-700 text-zinc-500 bg-white/[0.02]">OFF</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Row 2c: User Activity + Queue Runtime */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* User Activity */}
        <div className="jp-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
              <Users size={12} /> User Activity
            </h3>
            <RangePills value={activityRange} onChange={setActivityRange} />
          </div>
          {activityLoading ? (
            <div className="px-5 py-8 text-xs text-zinc-600 italic text-center">Loading…</div>
          ) : userActivity ? (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Total Users', userActivity.total_users],
                  ['Searching Now', userActivity.active_searching_now],
                  [`Active (${activityRange})`, userActivity.active_in_range],
                  [`Searches (${activityRange})`, userActivity.total_searches_in_range],
                ].map(([label, val]) => (
                  <div key={label} className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-amber-400 font-mono">{val ?? 0}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
              {userActivity.per_user?.length > 0 && (
                <div>
                  <div className="text-[10px] text-zinc-500 mb-2 uppercase tracking-wider">Top Searchers</div>
                  <div className="space-y-1">
                    {userActivity.per_user.slice(0, 5).map((u, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="font-mono text-zinc-500 truncate max-w-[160px]">{u.user_id || 'unknown'}</span>
                        <span className="font-mono text-zinc-300 shrink-0">{u.search_count} searches</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="px-5 py-8 text-xs text-zinc-600 italic text-center">No data</div>
          )}
        </div>

        {/* Queue Runtime */}
        <div className="jp-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
              <Queue size={12} /> Queue Runtime
              <span className="text-[9px] text-zinc-600">(10s refresh)</span>
            </h3>
            <button onClick={loadQueueStats} className="p-1 hover:bg-white/5 rounded transition-colors">
              <ArrowClockwise size={12} />
            </button>
          </div>
          {queueStats ? (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-red-400 font-mono">{queueStats.unscored_jobs ?? 0}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">Unscored Jobs</div>
                </div>
                <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-amber-400 font-mono">
                    {queueStats.celery_active?.reduce((a, w) => a + w.count, 0) ?? 0}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">Active Tasks</div>
                </div>
              </div>
              {queueStats.recent_searches?.length > 0 && (
                <div>
                  <div className="text-[10px] text-zinc-500 mb-2 uppercase tracking-wider">Recent Searches</div>
                  <div className="space-y-1">
                    {queueStats.recent_searches.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                          s.status === 'complete' ? 'text-emerald-400' :
                          s.status === 'searching' ? 'text-amber-400' : 'text-zinc-500'
                        }`}>{s.status}</span>
                        <span className="font-mono text-zinc-400">{s.jobs_found ?? 0} jobs</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {queueStats.celery_active?.length === 0 && (
                <div className="text-[10px] text-zinc-600 italic">Celery workers idle</div>
              )}
            </div>
          ) : (
            <div className="px-5 py-8 text-xs text-zinc-600 italic text-center">No queue data</div>
          )}
        </div>
      </div>

      {/* Row 3: Resend + Slow Queries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Resend usage */}
        <div className="jp-card p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
            <EnvelopeSimple size={12} /> Resend Email Consumption
          </h3>
          {resend ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[['Today', resend.today], ['This Month', resend.month]].map(([label, val]) => (
                  <div key={label} className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-amber-400 font-mono">{val ?? 0}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] text-zinc-500 mb-2">30-day history</div>
                <Sparkline data={resend.sparkline} />
              </div>
            </div>
          ) : (
            <div className="text-xs text-zinc-600 italic">{loading ? 'Loading…' : 'No data'}</div>
          )}
        </div>

        {/* Slow queries */}
        <div className="jp-card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
              <Timer size={12} /> Top Slow Queries
            </h3>
          </div>
          <div className="overflow-y-auto max-h-64">
            {(slowQueries.length === 0) ? (
              <div className="px-4 py-6 text-xs text-zinc-600 italic text-center">
                {loading ? 'Loading…' : slowQ === null ? 'Query profiler disabled' : 'No slow queries recorded'}
              </div>
            ) : (
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="bg-white/[0.02] text-zinc-500 sticky top-0">
                    <th className="px-3 py-2 font-semibold">Table / Op</th>
                    <th className="px-3 py-2 font-semibold text-right">Avg ms</th>
                    <th className="px-3 py-2 font-semibold text-right">Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {slowQueries.map((q, i) => (
                    <tr key={i} className="hover:bg-white/[0.02]">
                      <td className="px-3 py-2">
                        <div className="text-zinc-300">{q._id?.table || q.table || q._id?.collection || q.collection || '—'}</div>
                        <div className="text-zinc-600 font-mono truncate max-w-[160px]">
                          {q._id?.operation || q.operation || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-amber-400">
                        {q.avg_ms != null ? q.avg_ms.toFixed(0) : (q.duration_ms ?? '—')}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-400">
                        {q.count ?? 1}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
